import NotificationBar from '../../shared/ui-components/NotificationBar/NotificationBar';
import { useDispatch, useSelector } from 'react-redux';
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { DOKUMENTART, dokumenteDownload, DokumentSelectors, sortedDokumentartOptions } from '../../store/Dokument.store';
import type { Dokument } from '../../shared/types';
import { localStorageWrapper } from '../../localStorage';
import LoadingSpinner from '../../shared/ui-components/LoadingSpinner/LoadingSpinner';
import { OverviewPagination } from '../../shared/ui-components/OverviewPagination/OverviewPagination';
import { OverviewTable } from '../../shared/ui-components/OverviewTable/OverviewTable';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import { usePersistedState } from '../../shared/hooks/usePersistedState';
import { useScrollRestoration } from '../../shared/hooks/useScrollRestoration';
import Select, { OptionTypeBase } from 'react-select';
import {
    createAbsenderColumn,
    createBelegDatumColumn,
    createBelegNummerColumn,
    createDokumentArtColumn,
    createDokumentenCheckBox,
    createDownloadLinkColumn,
} from './Columns';
import Button from '../../shared/ui-components/Button/Button';
import { SearchInput } from '../../shared/ui-components/SearchInput/SearchInput';
import { useFetchDokumente } from './useFetchDokumente';
import { useDebounce } from '../../shared/hooks/useDebounce';
import { OverviewList } from '../../shared/ui-components/OverviewList/OverviewList';
import { Breakpoint, useBreakpoint } from '../../shared/hooks/useBreakpoint';
import { FilterPanel, FilterPanelAlignment } from '../../shared/ui-components/Filter/FilterPanel';
import { createCustomStyles } from '../../shared/ui-components/SelectInput/CustomStyles';

const PAGE_SIZE_KEY = 'Dokumentenuebersicht.pageSize';
const PAGE_INDEX_KEY = 'Dokumentenuebersicht.page';
const PAGE_SORTBY_KEY = 'Dokumentenuebersicht.sortBy';

const DEBOUNCE_INPUT_TIME_IN_MS = 400;

const Dokumentenuebersicht = (): ReactElement => {
    const pageTitle = 'Meine Dokumente';
    const dispatch = useDispatch();
    const isMobile = useBreakpoint(Breakpoint.MOBILE);

    useDocumentTitle(pageTitle);
    const [pageSizeState, setPageSizeState] = useState<number>(parseInt(localStorageWrapper.getItem(PAGE_SIZE_KEY) || '10'));
    const dokumente = useSelector(DokumentSelectors.dokumente);
    const isLoading = useSelector(DokumentSelectors.dokumenteLoading);
    const totalCount = useSelector(DokumentSelectors.totalCount);
    const loadFailed = useSelector(DokumentSelectors.dokumenteFailed);
    const noDokumentSelected = useSelector(DokumentSelectors.isSelectedDokumenteEmpty);
    const [dokumentartFilterOption, setDokumentartFilterOption] = usePersistedState<OptionTypeBase | undefined>(
        'Dokumentenuebersicht.dokumentartFilter',
        undefined
    );
    const columns: Column<Dokument>[] = useMemo(
        () => [
            createDokumentenCheckBox(),
            createDokumentArtColumn(),
            createAbsenderColumn(),
            createBelegNummerColumn(),
            createBelegDatumColumn(),
            createDownloadLinkColumn(),
        ],
        []
    );
    const initialPageCount = Math.ceil(totalCount / pageSizeState);
    const persistedSortBy = JSON.parse(localStorageWrapper.getItem(PAGE_SORTBY_KEY) || '[]')[0];
    const tableInstance = useTable<Dokument>(
        {
            columns,
            data: dokumente,
            initialState: {
                sortBy: [
                    {
                        id: persistedSortBy?.id ?? 'belegDatum',
                        // ?? statt || damit ein bewusst gespeichertes desc=false (aufsteigend) erhalten bleibt.
                        desc: persistedSortBy?.desc ?? true,
                    },
                ],
                pageSize: pageSizeState,
                pageIndex: parseInt(localStorageWrapper.getItem(PAGE_INDEX_KEY) || '0'),
            },
            pageCount: initialPageCount,
            manualPagination: true,
            manualSortBy: true,
            disableMultiSort: true,
            disableSortRemove: true,
        },
        useSortBy,
        usePagination
    );
    const { state, gotoPage } = tableInstance;
    const [keyword, setKeyword] = usePersistedState<string | undefined>('Dokumentenuebersicht.keyword', undefined);
    const debouncedKeyword = useDebounce(keyword, DEBOUNCE_INPUT_TIME_IN_MS);

    useScrollRestoration('Dokumentenuebersicht', !isLoading && dokumente.length > 0);

    useEffect(() => {
        gotoPage(0);
    }, [debouncedKeyword, gotoPage]);

    const reloadDokumente = useFetchDokumente(
        dokumentartFilterOption?.value as DOKUMENTART,
        state.sortBy,
        state.pageSize,
        state.pageIndex,
        debouncedKeyword
    );

    return (
        <>
            <h2>{pageTitle}</h2>
            <NotificationBar
                testId="loadError-message-bar"
                message="Die Dokumente konnten nicht geladen werden. Bitte versuchen Sie es noch einmal."
                isVisible={loadFailed}
                actionText="Neu laden"
                actionCallback={reloadDokumente}
            />
            <FilterPanel alignment={FilterPanelAlignment.SPACED}>
                <FilterPanel.Entry>
                    <Button
                        onClick={() => {
                            dispatch(dokumenteDownload());
                        }}
                        disabled={noDokumentSelected}
                        isFullWidth
                    >
                        Download Dokumente
                    </Button>
                </FilterPanel.Entry>
                <FilterPanel.Entry col={3}>
                    <SearchInput searchAction={reloadDokumente} keyword={keyword} setKeyword={setKeyword} />
                </FilterPanel.Entry>
                <FilterPanel.Entry>
                    <Select
                        options={sortedDokumentartOptions}
                        name="dokumentartFilter"
                        value={dokumentartFilterOption}
                        onChange={(value) => {
                            setDokumentartFilterOption(value);
                            gotoPage(0);
                        }}
                        styles={createCustomStyles(false, false, 'left')}
                        placeholder="Dokumentart Filter"
                        menuShouldScrollIntoView
                        isClearable
                        menuPlacement={'auto'}
                    />
                </FilterPanel.Entry>
            </FilterPanel>
            <LoadingSpinner isLoading={isLoading && dokumente.length === 0}>
                <>
                    {isMobile ? (
                        <OverviewList
                            tableInstance={tableInstance}
                            headerColumns={['belegDatum']}
                            mobileColumns={['checkBox', 'dokumentArt', 'dokumentAbsender', 'dokumentBelegNummer', 'belegDatum', 'actions']}
                        />
                    ) : (
                        <OverviewTable tableInstance={tableInstance} />
                    )}
                    {dokumente.length > 0 && (
                        <OverviewPagination tableInstance={tableInstance} setPageSizeState={setPageSizeState} totalCount={totalCount} />
                    )}
                </>
            </LoadingSpinner>
        </>
    );
};

export default Dokumentenuebersicht;
